import CookieOverlay from 'components/CookieOverlay';
import CheckoutRoutes from 'modules/checkout';
import ConfirmationRoute from 'modules/confirmation';
import ExperienceRoutes from 'modules/experience';
import FeatureFlagRoute from 'modules/feature-flags';
import InfoPagesRoutes from 'modules/info-pages';
import PersonalizedOfferRoutes from 'modules/host-personalized-offer';
import PrivacyRoute from 'modules/privacy';
import PasswordRoutes from 'modules/authentication/PasswordRoutes';
import HostAgendaRoutes from 'modules/host-agenda';
import RateAppRoute from 'modules/rate-app';
import RescheduleRoutes from 'modules/reschedule';
import ReviewPageRoute from 'modules/review-page';
import HomescreenRoutes from 'modules/homescreen';
import SearchRoutes from 'modules/search';
import UserEnvironment2Routes from 'modules/user-environment2';
import ManageBookingRoutes from 'modules/manage-booking';
import HostHomePage from 'modules/host-homescreen';
import HostLearningCenter from 'modules/host-learning-center';
import HostRegistration from 'modules/host-registration';
import HostExperiencesRoutes from 'modules/host-experiences';
import HostStatusRoutes from 'modules/host-status';
import HostProfile from 'modules/host-profile';
import HostRoute from 'modules/host';
import HostSurvey from 'modules/host-survey';
import HostInbox from 'modules/host-inbox';
import HostTransferBooking from 'modules/host-transfer-booking';
import Notfound from 'modules/errorpages/Notfound';
import ServerError from 'modules/errorpages/ServerError';
import AppPromoRoute from 'modules/landings/app-promo';
import { UserEnvironmentContextProvider } from '~/context/UserEnvironmentContext';
import React, { useEffect, useRef, useState } from 'react';
import { Switch, withRouter } from 'react-router-dom';
import DefaultHead from 'modules/head';
import BackUrlHandler from './BackUrlHandler';
import ModalOverlays from 'modules/app/ModalOverlays';
import FavoritesRoutes from 'modules/favorites';
import { WlWindow } from '../../../typings/globals/cordova';
import { scrollToFocusedOnKeyboard } from 'utils/onResize';
import ExperiencesRoutes from 'modules/experiences';
import LocalSearchRoutes from 'modules/locals';
import PrismicRoutes from 'modules/prismic';
import WriteReviewRoutes from 'modules/write-review';
import ScrollToTop from 'components/ScrollToTop';
import AbTestsRoute from 'modules/ab-tests';
import { isCordova } from 'utils/platform';
import { FavoritesContextProvider } from 'providers/FavoritesProvider';
import OverviewPageRoutes from 'modules/overview';
import { AuthContextProvider } from '~/context/AuthContext';
import HostPayoutRoutes from 'modules/host-payout-methods';
import InvitationRoutes from 'modules/invite';
import { CurrencyContextProvider } from '~/context/CurrencyContext';
import SustainabilityRoute from 'modules/landings/sustainability';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import CourseRoutes from 'modules/course';
import { CookieConsentContextProvider } from 'providers/CookieConsentProvider';
import { TrackRouteChanges } from 'hooks/useTrackRouteChanges';
import { NotificationsProvider } from '~/context/NotificationsContext';
import HostOnboardingPageRoute from 'modules/host-onboarding';
import { ExperimentsContextProvider } from '~/context/ExperimentsContext';
import useAuthStore from '~/authentication/store';
import LoadingDots from 'components/LoadingDots';

declare const window: WlWindow;

const queryClient = new QueryClient();

const App = ({ location, history }) => {
  const previousLocation = useRef(location);
  const [newLocation, setNewLocation] = useState(null);
  const hasHydrated = useAuthStore(state => state._hasHydrated);
  const [isModal, setIsModal] = useState(false);

  useEffect(() => {
    if (isCordova() && hasHydrated) {
      window.navigator.splashscreen.hide();
      setTimeout(() => {
        window.StatusBar.overlaysWebView(false);
        window.StatusBar.styleDefault();
        window.StatusBar.show();
      });
    }
    const unsubscribe = scrollToFocusedOnKeyboard();
    return () => {
      unsubscribe();
    };
  }, [hasHydrated]);

  useEffect(() => {
    const becomesModal = !!location?.state?.modal && !previousLocation.current?.state?.modal;
    if (becomesModal) {
      setNewLocation(previousLocation.current);
    } else {
      setNewLocation(location);
    }
    setIsModal(becomesModal);
    previousLocation.current = location;
  }, [location]);

  if (!hasHydrated) {
    return <LoadingDots />;
  }

  return (
    <CookieConsentContextProvider>
      <AuthContextProvider>
        <QueryClientProvider client={queryClient} key="query-client">
          <ExperimentsContextProvider>
            <UserEnvironmentContextProvider>
              <NotificationsProvider>
                <CurrencyContextProvider>
                  <FavoritesContextProvider>
                    <ScrollToTop isModal={isModal} history={history} newLocation={newLocation} />
                    <DefaultHead />
                    <BackUrlHandler />
                    <TrackRouteChanges />
                    <Switch location={newLocation}>
                      {PrismicRoutes}
                      {HomescreenRoutes}
                      {HostHomePage}
                      {HostOnboardingPageRoute}
                      {HostLearningCenter}
                      {HostRegistration}
                      {HostProfile}
                      {HostSurvey}
                      {HostInbox}
                      {HostTransferBooking}
                      {SearchRoutes}
                      {ExperiencesRoutes}
                      {LocalSearchRoutes}
                      {ExperienceRoutes}
                      {CheckoutRoutes}
                      {ConfirmationRoute}
                      {HostAgendaRoutes}
                      {RateAppRoute}
                      {ReviewPageRoute}
                      {FavoritesRoutes}
                      {CourseRoutes}
                      {PrivacyRoute}
                      {AppPromoRoute}
                      {PersonalizedOfferRoutes}
                      {HostPayoutRoutes}
                      {HostExperiencesRoutes}
                      {HostStatusRoutes}
                      {UserEnvironment2Routes}
                      {HostRoute}
                      {ModalOverlays}
                      {FeatureFlagRoute}
                      {AbTestsRoute}
                      {SustainabilityRoute}
                      {WriteReviewRoutes}
                      {InfoPagesRoutes}
                      {PasswordRoutes}
                      {ManageBookingRoutes}
                      {OverviewPageRoutes}
                      {InvitationRoutes}
                      {RescheduleRoutes}
                      {ServerError}
                      <Notfound />
                    </Switch>
                    <CookieOverlay />
                    {isModal &&
                      <Switch>
                        {PrismicRoutes}
                        {HostHomePage}
                        {HostOnboardingPageRoute}
                        {HomescreenRoutes}
                        {HostLearningCenter}
                        {HostExperiencesRoutes}
                        {HostStatusRoutes}
                        {HostProfile}
                        {HostSurvey}
                        {HostInbox}
                        {HostTransferBooking}
                        {SearchRoutes}
                        {ExperiencesRoutes}
                        {LocalSearchRoutes}
                        {ExperienceRoutes}
                        {CheckoutRoutes}
                        {ConfirmationRoute}
                        {HostAgendaRoutes}
                        {RateAppRoute}
                        {ReviewPageRoute}
                        {FavoritesRoutes}
                        {CourseRoutes}
                        {PrivacyRoute}
                        {AppPromoRoute}
                        {PersonalizedOfferRoutes}
                        {UserEnvironment2Routes}
                        {ManageBookingRoutes}
                        {UserEnvironment2Routes}
                        {HostRoute}
                        {ModalOverlays}
                        {FeatureFlagRoute}
                        {AbTestsRoute}
                        {WriteReviewRoutes}
                        {InfoPagesRoutes}
                        {PasswordRoutes}
                        {OverviewPageRoutes}
                        {RescheduleRoutes}
                      </Switch>
                    }
                  </FavoritesContextProvider>
                </CurrencyContextProvider>
              </NotificationsProvider>
            </UserEnvironmentContextProvider>
          </ExperimentsContextProvider>
        </QueryClientProvider>
      </AuthContextProvider>
    </CookieConsentContextProvider>
  );
};

export default withRouter(App);
